import { useGetIdentity } from "@refinedev/core";

import type { AuthIdentity } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { useApiAction } from "../_shared/useApiAction";
import {
  actionCardPrimaryLabel,
  canAcceptActionCard,
  canLifecycleAction,
  canReopenActionCard,
  resolveActionActorId,
  type ActionCardActionTarget,
} from "./model";

interface ActionCardLifecycleActionsProps {
  card: ActionCardActionTarget;
  onChanged?: () => void;
  compact?: boolean;
}

function deferUntilIso(): string {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString();
}

export function ActionCardLifecycleActions({
  card,
  onChanged,
  compact,
}: ActionCardLifecycleActionsProps) {
  const { data: identity } = useGetIdentity<AuthIdentity>();
  const { run, loading } = useApiAction();
  const actorId = resolveActionActorId(identity);
  const actionable = canLifecycleAction(card);
  const acceptDisabled = loading || !actorId || !canAcceptActionCard(card);
  const transitionDisabled = loading || !actorId || !actionable;
  const reopenDisabled = loading || !actorId || !canReopenActionCard(card);

  const mutate = async (
    verb: "accept" | "reject" | "defer" | "reopen",
    body: Record<string, unknown>,
    successMessage: string,
  ) => {
    if (!actorId) return;
    await run(`/action-cards/${encodeURIComponent(card.id)}/${verb}`, {
      body: { actor_id: actorId, ...body },
      invalidateResources: ["action-cards", "events", "contexts"],
      successMessage,
    });
    onChanged?.();
  };

  const accept = () => {
    void mutate("accept", {}, "Action card accepted.");
  };

  const reject = () => {
    void mutate(
      "reject",
      { reason: "Rejected from the portal recommendation stream." },
      "Action card rejected.",
    );
  };

  const defer = () => {
    void mutate(
      "defer",
      {
        reason: "Deferred for the next review window.",
        defer_until: deferUntilIso(),
      },
      "Action card deferred.",
    );
  };

  const reopen = () => {
    void mutate("reopen", { reason: "Reopened from the portal." }, "Action card reopened.");
  };

  const title = actorId
    ? undefined
    : "Cannot act until the current identity resolves to an actor.";

  if (compact) {
    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={acceptDisabled} onClick={accept} title={title}>
          <Icon name="approve" size={16} className="mr-2" aria-hidden />
          {actionCardPrimaryLabel(card)}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={transitionDisabled} onClick={defer} title={title}>
          <Icon name="defer" size={16} className="mr-2" aria-hidden />
          Defer 7 days
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={transitionDisabled}
          className="text-destructive focus:text-destructive"
          onClick={reject}
          title={title}
        >
          <Icon name="reject" size={16} className="mr-2" aria-hidden />
          Reject
        </DropdownMenuItem>
        {canReopenActionCard(card) ? (
          <DropdownMenuItem disabled={reopenDisabled} onClick={reopen} title={title}>
            <Icon name="reopen" size={16} className="mr-2" aria-hidden />
            Reopen
          </DropdownMenuItem>
        ) : null}
      </>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" onClick={accept} disabled={acceptDisabled} title={title}>
        <Icon name="approve" size={16} className="mr-2" aria-hidden />
        {actionCardPrimaryLabel(card)}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={defer}
        disabled={transitionDisabled}
        title={title}
      >
        <Icon name="defer" size={16} className="mr-2" aria-hidden />
        Defer
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={reject}
        disabled={transitionDisabled}
        title={title}
      >
        <Icon name="reject" size={16} className="mr-2" aria-hidden />
        Reject
      </Button>
      {canReopenActionCard(card) ? (
        <Button
          size="sm"
          variant="outline"
          onClick={reopen}
          disabled={reopenDisabled}
          title={title}
        >
          <Icon name="reopen" size={16} className="mr-2" aria-hidden />
          Reopen
        </Button>
      ) : null}
    </div>
  );
}
